<template>
  <div>
    <!-- Desktop Sidebar -->
    <div class="sidebar-container">
      <!-- Fixed Main Sidebar -->
      <div class="main-sidebar">
        <div class="logo-container">
          <img src="@/assets/logosimbolo.png" alt="Logo" class="logo-img" />
        </div>

        <!-- Menu Icons -->
        <div class="icons-list">
          <template v-for="(menu, index) in menus">
            <div
              v-if="menu.show"
              :key="index"
              class="icon-item"
              :class="{ 'active': isMenuItemActive(menu) || hoveredItem === menu }"
              @click="toggleFlyout(menu)"
            >
              <v-icon :color="isMenuItemActive(menu) || hoveredItem === menu ? 'white' : 'grey'">{{ menu.iconp }}</v-icon>
              <v-tooltip right>
                <template v-slot:activator="{ on, attrs }">
                  <div v-bind="attrs" v-on="on" class="tooltip-trigger"></div>
                </template>
                <span>{{ menu.title }}</span>
              </v-tooltip>
            </div>
          </template>
        </div>

        <!-- User Avatar -->
        <div class="user-avatar-container">
          <v-avatar size="40" class="cursor-pointer hover:scale-105 transition-transform" @click="openProfileModal">
             <img
              :src="
                $store.getters.getUserData.user_photo
                  ? $store.state.urlImages + $store.getters.getUserData.user_photo
                  : 'https://placehold.co/400'
              "
              :alt="$store.getters.getUserData.user_login"
            />
          </v-avatar>
          <v-tooltip right>
            <template v-slot:activator="{ on, attrs }">
              <div v-bind="attrs" v-on="on" class="tooltip-trigger" @click="openProfileModal"></div>
            </template>
            <span>Perfil</span>
          </v-tooltip>
        </div>
      </div>

      <!-- User Profile Modal -->
      <v-dialog v-model="profileModal" max-width="500px" persistent>
        <v-card class="rounded-xl overflow-hidden border-none shadow-2xl">
          <!-- Modal Header -->
          <div class="px-6 py-4 flex items-center justify-between" style="background-color: #313131;">
            <div class="flex items-center gap-3">
              <v-icon color="orange" small>mdi-account-edit-outline</v-icon>
              <h2 class="text-white font-bold uppercase tracking-widest text-[11px]">EDITAR PERFIL</h2>
            </div>
            <v-btn icon dark small @click="profileModal = false">
              <v-icon small>mdi-close</v-icon>
            </v-btn>
          </div>

          <div class="p-8 pb-4">
            <div class="flex flex-col items-center gap-6">
              <!-- Photo Upload -->
              <div class="relative group cursor-pointer" @click="$refs.profileFileInput.click()">
                <v-avatar size="120" class="border-4 border-slate-50 shadow-md transition-transform group-hover:scale-[1.02] bg-slate-100">
                  <img
                    :src="profileData.user_photo ? $store.state.urlImages + profileData.user_photo : 'https://placehold.co/400'"
                    class="object-cover"
                  />
                </v-avatar>
                <div class="absolute inset-0 bg-black/40 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                  <v-icon color="white">mdi-camera-plus</v-icon>
                </div>
                <input
                  type="file"
                  accept="image/jpeg, image/png"
                  @change="uploadImage"
                  ref="profileFileInput"
                  style="display: none"
                />
              </div>

              <div class="w-full space-y-4">
                <!-- Nome/Login Editable -->
                <div class="grid grid-cols-1 gap-4">
                  <div>
                    <label class="text-[10px] font-bold text-slate-400 uppercase tracking-widest ml-1 mb-1 block">Nome de Usuário</label>
                    <input 
                      type="text" 
                      v-model="profileData.user_login"
                      class="w-full bg-slate-50 border border-slate-200 rounded px-3 py-2.5 text-sm text-slate-700 focus:outline-none focus:border-orange-500 focus:ring-1 focus:ring-orange-500 transition-colors uppercase"
                    >
                  </div>

                  <div>
                    <label class="text-[10px] font-bold text-slate-400 uppercase tracking-widest ml-1 mb-1 block">Senha</label>
                    <div class="relative">
                      <input 
                        :type="showPwd ? 'text' : 'password'" 
                        v-model="profileData.user_password"
                        placeholder="Deixe em branco para não alterar"
                        class="w-full bg-slate-50 border border-slate-200 rounded px-3 py-2.5 text-sm text-slate-700 focus:outline-none focus:border-orange-500 focus:ring-1 focus:ring-orange-500 transition-colors"
                      >
                      <v-btn icon small class="absolute right-2 top-1.5" @click="showPwd = !showPwd">
                        <v-icon small color="grey">{{ showPwd ? 'mdi-eye' : 'mdi-eye-off' }}</v-icon>
                      </v-btn>
                    </div>
                  </div>
                </div>

                <!-- Read-only info -->
                <v-divider class="my-4"></v-divider>
                
                <div class="grid grid-cols-1 gap-3">
                  <div class="flex justify-between items-center text-[10px] py-1">
                    <span class="font-bold text-slate-400 uppercase tracking-widest">Login (Fixo)</span>
                    <span class="font-bold text-slate-600 uppercase">{{ $store.getters.getUserData.user_login }}</span>
                  </div>
                  <div class="flex justify-between items-center text-[10px] py-1 border-t border-slate-100">
                    <span class="font-bold text-slate-400 uppercase tracking-widest">Grupo</span>
                    <span class="font-bold text-slate-600 uppercase">{{ profileData.group_name || 'ADMINISTRADOR' }}</span>
                  </div>
                  <div class="flex justify-between items-center text-[10px] py-1 border-t border-slate-100">
                    <span class="font-bold text-slate-400 uppercase tracking-widest">Vínculo</span>
                    <span class="font-bold text-slate-600 uppercase">{{ profileData.ref ? (profileData.ref.name || profileData.ref.prefecture_social_name || profileData.ref.provider_name || 'SISTEMA') : 'NENHUM' }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Modal Footer -->
          <div class="bg-slate-50 px-8 py-5 border-t border-slate-200 flex justify-end items-center gap-4 mt-4">
            <button 
              type="button"
              @click="profileModal = false"
              class="text-slate-500 hover:text-slate-700 font-bold text-[11px] uppercase px-4 py-2 transition-colors flex-none"
            >
              Cancelar
            </button>
            <div class="flex-none">
              <button
                type="button"
                @click="saveProfile"
                :disabled="$store.state.loader"
                class="bg-orange-500 hover:bg-orange-600 text-white font-bold text-[11px] px-6 py-2.5 rounded shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all w-auto max-w-max whitespace-nowrap flex items-center gap-2"
              >
                <v-icon small color="white" class="mr-1" style="font-size: 14px;">mdi-check</v-icon>
                <span v-if="$store.state.loader" class="animate-spin h-3 w-3 border-2 border-white border-t-transparent rounded-full"></span>
                <span v-else>SALVAR ALTERAÇÕES</span>
              </button>
            </div>
          </div>
        </v-card>
      </v-dialog>

      <!-- Snackbar -->
      <v-snackbar v-model="profileSnack.show" :color="profileSnack.color" timeout="3000" top right>
        <div class="flex items-center gap-2">
          <v-icon small color="white">{{ profileSnack.color === 'success' ? 'mdi-check-circle' : 'mdi-alert-circle' }}</v-icon>
          <span class="text-xs font-bold uppercase tracking-wider">{{ profileSnack.message }}</span>
        </div>
      </v-snackbar>

      <!-- Flyout Menu -->
      <div class="flyout-menu" :class="{ 'show': hoveredItem }">
        <div class="flyout-content" v-if="hoveredItem">
          <!-- Background Icon -->
          <v-icon class="flyout-bg-icon">{{ hoveredItem.iconp }}</v-icon>
          
          <div class="flyout-header">
            <h3>{{ hoveredItem.title }}</h3>
          </div>

          <div class="flyout-links">
            <!-- Direct Access -->
            <div v-if="!hoveredItem.links.length && !hoveredItem.group" class="flyout-link-item" :class="{ 'active-link': isRouteActive(hoveredItem.route) }" @click="navigate(hoveredItem.route)">
              <v-icon small class="mr-2" :color="isRouteActive(hoveredItem.route) ? '#FF4600' : 'grey'">mdi-arrow-right</v-icon>
              <span class="uppercase">{{ hoveredItem.title }}</span>
            </div>

            <!-- Sub Items -->
            <template v-else>
              <div v-for="(link, ind) in hoveredItem.links" :key="ind">
                 <!-- Simple Link -->
                <div v-if="(!link.children || link.children.length === 0) && link.show !== false" class="flyout-link-item" :class="{ 'active-link': isRouteActive(link.route) }" @click="navigate(link.route)">
                   <v-icon small class="mr-2" :color="isRouteActive(link.route) ? '#FF4600' : 'grey'">mdi-circle-small</v-icon>
                   <span>{{ link.label || link.title }}</span>
                </div>

                <!-- Group Link -->
                <div v-else-if="link.show !== false">
                  <div class="flyout-group-header" @click="toggleGroup(ind)">
                    <div class="flyout-group-title">{{ link.label || link.title }}</div>
                    <v-icon small color="grey">{{ expandedGroups.includes(ind) ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
                  </div>
                  
                  <v-expand-transition>
                    <div v-if="expandedGroups.includes(ind)">
                      <template v-for="(child, childIndex) in link.children">
                        <div v-if="child.show !== false" :key="childIndex" class="flyout-link-item pl-6" :class="{ 'active-link': isRouteActive(child.route) }" @click="navigate(child.route)">
                          <v-icon small class="mr-2" :color="isRouteActive(child.route) ? '#FF4600' : 'grey'">mdi-minus</v-icon>
                          <span>{{ child.label || child.title }}</span>
                        </div>
                      </template>
                    </div>
                  </v-expand-transition>
                </div>
              </div>
            </template>
          </div>

          <!-- Version Display -->
          <div class="flyout-footer" v-if="hoveredItem.title === 'Configurações'">
             <span>VERSÃO: {{ $store.state.appVersion || '1.0.5' }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  props: {
    drawer: Boolean,
  },
  beforeCreate() {
    this.$http(`/access-groups/${this.$store.getters.getUserData.group_id}`)
      .then((r) => (this.userMenus = JSON.parse(r.data[0].group_permissions)))
      .catch(() => (this.userMenus = []));
  },
  created() {
    setTimeout(() => {
      this.userMenus.map((um) => {
        this.menus.map((m) => {
          const checkMatch = (t1, t2) => {
            const norm = t => t ? t.replace(/Almoxarifado/g, "Peças e Serviços").replace(/Almoxarifados/g, "Peças e Serviços") : '';
            return norm(t1) === norm(t2);
          };
          if (checkMatch(um.title, m.title) || (m.title === "Abastecimentos Concluídos" && um.title === "Abastecimentos")) {
            m.show = um.show;
          } else {
            m.links.map((l) => {
              if (checkMatch(l.title, um.title) || (l.title === "Abastecimentos Concluídos" && um.title === "Abastecimentos")) {
                l.show = um.show;
              } else {
                if (l.children) {
                  l.children.map((c) => {
                    if (checkMatch(c.title, um.title) || (c.title === "Abastecimentos Concluídos" && um.title === "Abastecimentos")) {
                      c.show = um.show;
                    }
                  });
                }
              }
            });
          }
        });
      });
      // Permite exibir menus com adminOnly exclusivamente para administradores (group_id = 1)
      this.menus.map((m) => {
        const isAdmin = this.$store.getters.getUserData.group_id === 1;
        
        if (isAdmin && m.adminOnly) {
          m.show = true;
        }

        if (m.links && m.links.length > 0) {
          m.links.map(l => {
            if (isAdmin && (m.adminOnly || l.adminOnly)) {
              l.show = true;
            }
            if (l.children && l.children.length > 0) {
              l.children.map(c => {
                if (isAdmin && (m.adminOnly || l.adminOnly || c.adminOnly)) {
                  c.show = true;
                }
              });
            }
          });
        }
      });

      // Garante que os grupos pai sejam exibidos se qualquer filho ou submenu for visível
      this.menus.map((m) => {
        if (m.links && m.links.length > 0) {
          let hasVisibleLink = false;
          m.links.map((l) => {
            if (l.children && l.children.length > 0) {
              const hasVisibleChild = l.children.some(c => c.show);
              if (hasVisibleChild) {
                l.show = true;
              }
            }
            if (l.show) {
              hasVisibleLink = true;
            }
          });
          if (hasVisibleLink) {
            m.show = true;
          }
        }
      });
    }, 1000);
  },
  data: () => ({
    hoveredItem: null,
    expandedGroups: [],
    userMenus: [],
    profileModal: false,
    profileData: {},
    showPwd: false,
    profileSnack: {
      show: false,
      message: "",
      color: "success",
    },
    menus: [
      {
        title: "Dashboard",
        iconp: "mdi-view-dashboard",
        route: "/dashboard",
        group: true,
        active: false,
        show: false,
        single: false,
        links: [
          {
            title: "Dashboard",
            route: "/dashboard",
            show: true,
          },
          {
            title: "Peças e Serviços",
            route: "/dashboard?tab=parts",
            show: true,
          }
        ],
      },
      {
        title: "Cadastros",
        icon: "mdi-chevron-up",
        "icon-alt": "mdi-chevron-down",
        iconp: "mdi-clipboard-check-multiple",
        group: true,
        active: false,
        show: false,
        single: false,
        links: [
          {
            title: "Veículos",
            icon: "mdi-chevron-up",
            "icon-alt": "mdi-chevron-down",
            iconp: "mdi-clipboard-check-multiple",
            group: true,
            active: false,
            show: false,
            children: [
              {
                icon: "mdi-chevron-right",
                title: "Gerenciar Veiculos",
                route: "/vehicles-manager",
                show: false,
              },
              {
                icon: "mdi-chevron-right",
                title: "Gerenciar Máquinas",
                route: "/machines-manager",
                show: false,
                adminOnly: true,
              },
              {
                icon: "mdi-chevron-right",
                title: "Situação da Frota",
                route: "/vehicles-manager/situacao",
                show: false,
              },
              {
                icon: "mdi-chevron-right",
                title: "Modelos",
                route: "/vehicles-manager/models",
                show: false,
              },
              {
                icon: "mdi-chevron-right",
                title: "Marcas",
                route: "/vehicles-manager/brands",
                show: false,
              },
              {
                icon: "mdi-chevron-right",
                title: "Cores",
                route: "/vehicles-manager/colors",
                show: false,
              },
            ],
          },
          {
            title: "Proprietario",
            icon: "mdi-chevron-up",
            "icon-alt": "mdi-chevron-down",
            iconp: "mdi-clipboard-check-multiple",
            group: true,
            show: false,
            active: false,
            children: [
              {
                icon: "mdi-chevron-right",
                title: "Gerenciar Proprietario",
                route: "/proprietario",
                show: false,
              },
            ],
          },
          {
            title: "Locador",
            icon: "mdi-chevron-up",
            "icon-alt": "mdi-chevron-down",
            iconp: "mdi-clipboard-check-multiple",
            group: true,
            show: false,
            active: false,
            children: [
              {
                icon: "mdi-chevron-right",
                title: "Gerenciar Locador",
                route: "/locador",
                show: false,
              },
            ],
          },

          {
            title: "Dependências",
            icon: "mdi-chevron-up",
            "icon-alt": "mdi-chevron-down",
            iconp: "mdi-clipboard-check-multiple",
            group: true,
            active: false,
            show: false,
            children: [
              {
                icon: "mdi-chevron-right",
                title: "Tipos de Atendimento",
                route: "/attendance-types",
                show: false,
              },
              {
                icon: "mdi-chevron-right",
                title: "Tipos de Combustível",
                route: "/fuels-types",
                show: false,
              },
              {
                icon: "mdi-chevron-right",
                title: "Tipos de Consumo",
                route: "/consumption-types",
                show: false,
              },
              {
                icon: "mdi-chevron-right",
                title: "Tipos de Vínculo",
                route: "/bonds-types",
                show: false,
              },
              {
                icon: "mdi-chevron-right",
                title: "Tipos de Peças e Serviços",
                route: "/biddings-items/stock-types",
                show: false,
              },
            ],
          },
          {
            title: "Fornecedores",
            icon: "mdi-chevron-up",
            "icon-alt": "mdi-chevron-down",
            iconp: "mdi-clipboard-check-multiple",
            group: true,
            show: false,
            active: false,
            children: [
              {
                icon: "mdi-chevron-right",
                title: "Gerenciar Fornecedores",
                route: "/providers-manager",
                show: false,
              },
            ],
          },
          {
            title: "Frentistas",
            icon: "mdi-chevron-up",
            "icon-alt": "mdi-chevron-down",
            iconp: "mdi-clipboard-check-multiple",
            group: true,
            show: false,
            active: false,
            children: [
              {
                icon: "mdi-chevron-right",
                title: "Gerenciar Frentistas",
                route: "/attendants-manager",
                show: false,
              },
            ],
          },
          {
            title: "Usuários",
            icon: "mdi-chevron-up",
            "icon-alt": "mdi-chevron-down",
            iconp: "mdi-clipboard-check-multiple",
            group: true,
            active: false,
            children: [
              {
                icon: "mdi-chevron-right",
                title: "Gerenciar Usuários",
                route: "/users-manager",
                show: false,
              },
              {
                icon: "mdi-chevron-right",
                title: "Grupos de Acesso",
                route: "/access-groups",
                show: false,
              },
            ],
          },
          {
            title: "Prefeituras",
            icon: "mdi-chevron-up",
            "icon-alt": "mdi-chevron-down",
            iconp: "mdi-clipboard-check-multiple",
            group: true,
            active: false,
            show: false,
            children: [
              {
                icon: "mdi-chevron-right",
                title: "Gerenciar Prefeituras",
                route: "/prefectures-manager",
                show: false,
              },
            ],
          },
          {
            title: "Motoristas",
            icon: "mdi-chevron-up",
            "icon-alt": "mdi-chevron-down",
            iconp: "mdi-clipboard-check-multiple",
            group: true,
            active: false,
            show: false,
            children: [
              {
                icon: "mdi-chevron-right",
                title: "Gerenciar Motoristas",
                route: "/drivers-manager",
                show: false,
              },
              {
                icon: "mdi-chevron-right",
                title: "Relatório de Motoristas",
                route: "/report/motoristas",
                show: false,
              },
            ],
          },
          {
            title: "Licitaçōes",
            icon: "mdi-chevron-up",
            "icon-alt": "mdi-chevron-down",
            iconp: "mdi-clipboard-check-multiple",
            group: true,
            show: false,
            active: false,
            children: [
              {
                icon: "mdi-chevron-right",
                title: "Gerenciar Licitaçōes",
                route: "/biddings-manager",
                show: false,
              },
              {
                icon: "mdi-chevron-right",
                title: "Tipos de Licitação",
                route: "/biddings-types",
                show: false,
              },
              {
                icon: "mdi-chevron-right",
                title: "Modelos de Licitação",
                route: "/biddings-modalities",
                show: false,
              },
            ],
          },
          {
            title: "Cartão Black",
            icon: "mdi-chevron-up",
            "icon-alt": "mdi-chevron-down",
            iconp: "mdi-card-bulleted-outline",
            group: true,
            show: false,
            active: false,
            children: [
              {
                icon: "mdi-chevron-right",
                title: "Gerenciar Clientes",
                route: "/settings/black-cards/clients",
                show: false,
              },
              {
                icon: "mdi-chevron-right",
                title: "Gerenciar Cartões",
                route: "/settings/black-cards",
                show: false,
              },
            ],
          },
        ],
      },
      {
        title: "Abastecimento",
        icon: "mdi-chevron-up",
        "icon-alt": "mdi-chevron-down",
        iconp: "mdi-gas-station",
        group: false,
        show: false,
        single: true,
        route: "/supplies-manager",
        links: [],
      },
      {
        title: "Conferência",
        icon: "mdi-chevron-up",
        "icon-alt": "mdi-chevron-down",
        iconp: "mdi-text-box-check-outline",
        group: false,
        show: false,
        single: true,
        route: "/supplies-manager/providers",
        links: [],
      },
      {
        title: "Abastecer",
        icon: "mdi-chevron-up",
        "icon-alt": "mdi-chevron-down",
        iconp: "mdi-gas-station",
        group: false,
        show: false,
        single: true,
        route: "/supplies-manager/attendants",
        links: [],
      },
      {
        title: "Abastecimentos pelo administrador",
        icon: "mdi-chevron-up",
        "icon-alt": "mdi-chevron-down",
        iconp: "mdi-gas-station",
        group: false,
        show: false,
        single: true,
        route: "/supplies-manager/master",
        links: [],
      },
      {
        title: "Relatórios pelo administrador",
        icon: "mdi-chevron-up",
        "icon-alt": "mdi-chevron-down",
        iconp: "mdi-chart-bar",
        group: true,
        active: false,
        show: false,
        single: false,
        links: [
          {
            title: "Conferências pelo administrador",
            icon: "mdi-chevron-up",
            "icon-alt": "mdi-chevron-down",
            iconp: "mdi-clipboard-check-multiple",
            group: false,
            active: false,
            show: false,
            route: "/conferences/master",
            children: [],
          },
          {
            title: "Bloqueados",
            icon: "mdi-chevron-up",
            "icon-alt": "mdi-chevron-down",
            iconp: "mdi-clipboard-check-multiple",
            group: false,
            active: false,
            show: false,
            route: "/blocked/master",
            children: [],
          },
          {
            title: "Reajuste",
            icon: "mdi-chevron-up",
            "icon-alt": "mdi-chevron-down",
            iconp: "mdi-clipboard-check-multiple",
            group: false,
            active: false,
            show: false,
            route: "/readjust/master",
            children: [],
          },
          {
            title: "Relatório Genérico",
            icon: "mdi-chevron-up",
            "icon-alt": "mdi-chevron-down",
            iconp: "mdi-calendar-check",
            group: false,
            active: false,
            show: false,
            route: "/report/master",
            children: [],
          },
          {
            title: "Retroatividade",
            icon: "mdi-chevron-up",
            "icon-alt": "mdi-chevron-down",
            iconp: "mdi-calendar-check",
            group: false,
            active: false,
            show: false,
            route: "/retroactivity/master",
            children: [],
          },
          {
            title: "Relatório TCE",
            icon: "mdi-chevron-up",
            "icon-alt": "mdi-chevron-down",
            iconp: "mdi-calendar-check",
            group: false,
            active: false,
            show: false,
            route: "/report/tce",
            children: [],
          },
          {
            title: "Relatório TCE: Proprietario Frota",
            icon: "mdi-chevron-up",
            "icon-alt": "mdi-chevron-down",
            iconp: "mdi-calendar-check",
            group: false,
            active: false,
            show: false,
            route: "/report/tce/proprietario-frota",
            children: [],
          },
          {
            title: "Relatório TCE: Locador Prestador",
            icon: "mdi-chevron-up",
            "icon-alt": "mdi-chevron-down",
            iconp: "mdi-calendar-check",
            group: false,
            active: false,
            show: false,
            route: "/report/tce/locador-prestador",
            children: [],
          },
          {
            title: "Relatório TCE: Veículos",
            icon: "mdi-chevron-up",
            "icon-alt": "mdi-chevron-down",
            iconp: "mdi-calendar-check",
            group: false,
            active: false,
            show: false,
            route: "/report/tce/veiculos",
            children: [],
          },
          {
            title: "Relatório TCE: Máquinas",
            icon: "mdi-chevron-up",
            "icon-alt": "mdi-chevron-down",
            iconp: "mdi-calendar-check",
            group: false,
            active: false,
            show: false,
            route: "/report/tce/maquinas",
            children: [],
          },
          {
            title: "Relatório TCE: Situação Frota",
            icon: "mdi-chevron-up",
            "icon-alt": "mdi-chevron-down",
            iconp: "mdi-calendar-check",
            group: false,
            active: false,
            show: false,
            route: "/report/tce/situacao-frota",
            children: [],
          },
          {
            title: "Relatório TCE: Abastecimento",
            icon: "mdi-chevron-up",
            "icon-alt": "mdi-chevron-down",
            iconp: "mdi-calendar-check",
            group: false,
            active: false,
            show: false,
            route: "/report/tce/abastecimento",
            children: [],
          },
          {
            title: "Veículos",
            icon: "mdi-chevron-up",
            "icon-alt": "mdi-chevron-down",
            iconp: "mdi-calendar-check",
            group: false,
            active: false,
            show: false,
            route: "/report/veiculos",
            children: [],
          },
          {
            title: "Motoristas",
            icon: "mdi-chevron-up",
            "icon-alt": "mdi-chevron-down",
            iconp: "mdi-calendar-check",
            group: false,
            active: false,
            show: false,
            route: "/report/motoristas",
            children: [],
          },
          {
            title: "Abastecimentos Concluídos",
            icon: "mdi-chevron-up",
            "icon-alt": "mdi-chevron-down",
            iconp: "mdi-calendar-check",
            group: false,
            active: false,
            show: false,
            route: "/report/supplies",
            children: [],
          },
        ],
      },
      {
        title: "Financeiro",
        icon: "mdi-chevron-up",
        "icon-alt": "mdi-chevron-down",
        iconp: "mdi-currency-usd",
        group: true,
        active: false,
        show: false,
        single: false,
        links: [
          {
            title: "Faturamento",
            icon: "mdi-chevron-up",
            "icon-alt": "mdi-chevron-down",
            iconp: "mdi-clipboard-check-multiple",
            group: false,
            active: false,
            show: false,
            route: "/billings",
            children: [],
          },
          {
            title: "Nota Fiscal",
            icon: "mdi-chevron-up",
            "icon-alt": "mdi-chevron-down",
            iconp: "mdi-clipboard-check-multiple",
            group: false,
            active: false,
            show: false,
            route: "/nfe",
            children: [],
          },
        ],
      },
      {
        title: "Peças e Serviços",
        icon: "mdi-chevron-up",
        "icon-alt": "mdi-chevron-down",
        iconp: "mdi-warehouse",
        group: true,
        active: false,
        show: false,
        single: false,
        links: [
          {
            title: "Fornecedores | Peças e Serviços",
            label: "Fornecedores",
            icon: "mdi-chevron-up",
            "icon-alt": "mdi-chevron-down",
            iconp: "mdi-clipboard-check-multiple",
            group: true,
            show: false,
            active: false,
            children: [
              {
                icon: "mdi-chevron-right",
                title: "Gerenciar Fornecedores | Peças e Serviços",
                label: "Gerenciar Fornecedores",
                route: "/parts-and-services/providers-manager",
                show: false,
              },
            ],
          },
          {
            title: "Licitações | Peças e Serviços",
            label: "Licitações",
            icon: "mdi-chevron-up",
            "icon-alt": "mdi-chevron-down",
            iconp: "mdi-clipboard-check-multiple",
            group: true,
            active: false,
            show: false,
            children: [
              {
                icon: "mdi-chevron-right",
                title: "Gerenciar Licitações | Peças e Serviços",
                label: "Gerenciar Licitações",
                route: "/parts-and-services/biddings-manager",
                show: false,
              },
            ],
          },
          {
            title: "Lançar Notas | Peças e Serviços",
            label: "Lançar Notas",
            icon: "mdi-chevron-up",
            "icon-alt": "mdi-chevron-down",
            iconp: "mdi-clipboard-check-multiple",
            group: true,
            show: false,
            active: false,
            children: [
              {
                icon: "mdi-chevron-right",
                title: "Gerenciar Notas | Peças e Serviços",
                label: "Gerenciar Notas",
                route: "/parts-and-services/nf",
                show: false,
              },
            ],
          },
          {
            title: "Ordem de Serviços | Peças e Serviços",
            label: "Ordem de Serviços",
            icon: "mdi-chevron-up",
            "icon-alt": "mdi-chevron-down",
            iconp: "mdi-file-document-edit",
            group: true,
            show: false,
            active: false,
            children: [
              {
                icon: "mdi-chevron-right",
                title: "Gerenciar OS | Peças e Serviços",
                label: "Gerenciar OS",
                route: "/parts-and-services/os",
                show: false,
              },
            ],
          },
          {
            title: "Liberação de OS | Peças e Serviços",
            label: "Liberação de OS",
            icon: "mdi-chevron-up",
            "icon-alt": "mdi-chevron-down",
            iconp: "mdi-shield-check-outline",
            group: true,
            show: false,
            active: false,
            children: [
              {
                icon: "mdi-chevron-right",
                title: "Gerenciar Liberação de OS | Peças e Serviços",
                label: "Painel de Liberação",
                route: "/parts-and-services/os-release",
                show: false,
              },
            ],
          },
          {
            title: "Relatórios | Peças e Serviços",
            label: "Relatórios Peças e Serviços",
            icon: "mdi-chevron-up",
            "icon-alt": "mdi-chevron-down",
            iconp: "mdi-clipboard-check-multiple",
            group: true,
            show: false,
            active: false,
            children: [
              {
                icon: "mdi-chevron-right",
                title: "Relatório Analítico | Peças e Serviços",
                label: "Relatório Analítico",
                route: "/parts-and-services/report/analytical",
                show: false,
              },
              {
                icon: "mdi-chevron-right",
                title: "Relatório Sintético | Peças e Serviços",
                label: "Relatório Sintético",
                route: "/parts-and-services/report/synthetic",
                show: false,
              },
            ],
          },
        ],
      },
      {
        title: "Histórico de Abastecimentos",
        icon: "mdi-chevron-up",
        "icon-alt": "mdi-chevron-down",
        iconp: "mdi-gas-station",
        group: true,
        show: false,
        single: true,
        links: [
          {
            title: "Abastecimentos Autorizados",
            icon: "mdi-chevron-up",
            "icon-alt": "mdi-chevron-down",
            iconp: "mdi-clipboard-check-multiple",
            group: false,
            active: false,
            show: false,
            route: "/authorizations",
            children: [],
          },
          {
            title: "Abastecimentos Extras",
            icon: "mdi-chevron-up",
            "icon-alt": "mdi-chevron-down",
            iconp: "mdi-clipboard-check-multiple",
            group: false,
            active: false,
            show: false,
            route: "/extra",
            children: [],
          },
        ],
      },
      {
        title: "Configurações",
        icon: "mdi-chevron-up",
        "icon-alt": "mdi-chevron-down",
        iconp: "mdi-cog",
        group: true,
        active: false,
        show: false,
        single: true,
        adminOnly: true,
        links: [
          {
            title: "Logs do Sistema",
            icon: "mdi-chevron-up",
            "icon-alt": "mdi-chevron-down",
            iconp: "mdi-clipboard-check-multiple",
            group: false,
            active: false,
            show: false,
            route: "/settings/logs",
            children: [],
          },
          {
            title: "Configuração de E-mail",
            icon: "mdi-chevron-up",
            "icon-alt": "mdi-chevron-down",
            iconp: "mdi-email-cog",
            group: false,
            active: false,
            show: false,
            route: "/settings/email",
            children: [],
          }
        ],
      },
    ],
  }),
  methods: {
    toggleFlyout(menu) {
       if (this.hoveredItem === menu) {
         this.hoveredItem = null;
         this.expandedGroups = [];
       } else {
         this.hoveredItem = menu;
         this.expandedGroups = [];
       }
    },
    toggleGroup(index) {
      if (this.expandedGroups.includes(index)) {
        this.expandedGroups = this.expandedGroups.filter(i => i !== index);
      } else {
        this.expandedGroups.push(index);
      }
    },
    isMenuItemActive(menu) {
      // 1. Verifica rota direta
      if (menu.route && this.$route.path === menu.route) return true;
      
      // 2. Verifica sublinks (recursivo)
      if (menu.links && menu.links.length > 0) {
        return menu.links.some(l => {
          if (l.route && this.$route.path === l.route) return true;
          if (l.children && l.children.length > 0) {
            return l.children.some(c => c.route && this.$route.path === c.route);
          }
          return false;
        });
      }
      return false;
    },
    isRouteActive(route) {
      return route && this.$route.fullPath === route;
    },
    navigate(route) {
      if (route && route !== this.$route.fullPath) {
        this.$router.push(route).catch(() => {});
        this.hoveredItem = null; // Close flyout on navigation
        this.expandedGroups = [];
      }
    },
    openProfileModal() {
      this.profileData = JSON.parse(JSON.stringify(this.$store.getters.getUserData));
      this.profileData.user_password = ""; // Don't show current password
      
      // Attempt to get group name if not present
      if (!this.profileData.group_name) {
        this.$http.get(`/access-groups/${this.profileData.group_id}`).then(r => {
          if (r.data.length > 0) {
            this.$set(this.profileData, 'group_name', r.data[0].group_name);
          }
        });
      }
      
      this.profileModal = true;
    },
    uploadImage(event) {
      this.$store.dispatch("setLoader", true);
      const fd = new FormData();
      fd.append("image", event.target.files[0], event.target.files[0].name);
      this.$http
        .post("/upload", fd)
        .then((r) => {
          this.profileData.user_photo = r.data.cover;
          this.$store.dispatch("setLoader", false);
        })
        .catch(() => {
          this.profileSnack.show = true;
          this.profileSnack.message = "Erro no upload!";
          this.profileSnack.color = "error";
          this.$store.dispatch("setLoader", false);
        });
    },
    saveProfile() {
      if (!this.profileData.user_login) {
        this.profileSnack.show = true;
        this.profileSnack.message = "Nome é obrigatório!";
        this.profileSnack.color = "warning";
        return;
      }

      this.$store.dispatch("setLoader", true);
      
      // Filter only allowed columns for the users table
      const allowedColumns = ['user_id', 'user_login', 'user_password', 'user_photo', 'group_id', 'table_id', 'table_key'];
      const updateData = {};
      
      allowedColumns.forEach(col => {
        if (this.profileData[col] !== undefined) {
          updateData[col] = this.profileData[col];
        }
      });

      if (!updateData.user_password) {
        delete updateData.user_password;
      }

      this.$http.put(`/users/${updateData.user_id}`, updateData)
        .then(() => {
          // Update Vuex and sessionStorage
          const userData = { ...this.$store.getters.getUserData, ...updateData };
          delete userData.user_password; // Security
          this.$store.dispatch("userData", userData);
          sessionStorage.setItem("userData", JSON.stringify(userData));
          
          this.profileSnack.show = true;
          this.profileSnack.message = "Perfil atualizado com sucesso!";
          this.profileSnack.color = "success";
          this.profileModal = false;
          this.$store.dispatch("setLoader", false);
        })
        .catch(() => {
          this.profileSnack.show = true;
          this.profileSnack.message = "Erro ao salvar!";
          this.profileSnack.color = "error";
          this.$store.dispatch("setLoader", false);
        });
    }
  }
};
</script>

<style scoped>
/* Mobile / Original Styles */
.list {
  border-bottom: 1px solid rgb(70, 70, 70);
}
.active {
  color: red;
}
.container {
  display: flex;
  justify-content: center;
  width: 100%;
  color: #fff;
  text-align: center;
  padding: 30px 10px;
  border-bottom: 1px solid rgb(70, 70, 70);
  font-size: 1.2rem;
}
.appName {
  padding: 10;
  margin-bottom: 30px;
  font-size: 1.2rem;
  font-weight: bold;
}
.userName {
  margin-top: 15px;
}

/* Desktop Sidebar Styles */
.sidebar-container {
  position: fixed;
  top: 0;
  left: 0;
  height: 100vh;
  z-index: 999;
  display: flex;
}

.main-sidebar {
  width: 80px;
  height: 100%;
  background-color: #333333;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  padding: 10px 0;
  z-index: 1000;
  position: relative;
}

.logo-container {
  margin-top: 10px;
  margin-bottom: 30px;
}

.logo-img {
  width: 45px;
  height: auto;
  object-fit: contain;
}

.icons-list {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  padding: 0 5px;
  overflow-y: auto;
  flex-grow: 1; /* Take available space */
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE */
}
.icons-list::-webkit-scrollbar {
  display: none; /* Chrome */
}

.icon-item {
  width: 50px;
  height: 50px;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 10px;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative; /* Added to constrain absolute children (tooltip-trigger) */
}

.icon-item:hover {
  background-color: rgba(255, 255, 255, 0.1);
}

.icon-item.active {
  background-color: #FF4600;
  box-shadow: 0 4px 6px rgba(0,0,0,0.1);
}

.tooltip-trigger {
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;
}

.user-avatar-container {
  margin-bottom: 20px;
  position: relative;
}

/* Flyout Styles */
.flyout-menu {
  position: absolute;
  left: 80px;
  top: 0;
  height: 100%;
  background-color: #2a2a2a;
  overflow: hidden;
  width: 0;
  opacity: 0;
  transition: width 0.3s ease, opacity 0.3s ease;
  z-index: 998;
}

.flyout-menu.show {
  width: 256px;
  opacity: 1;
}

.flyout-content {
  width: 256px; /* Fixed content width to prevent reflow during transition */
  height: 100%;
  padding: 20px;
  position: relative;
  overflow-y: auto;
  background-color: #2a2a2a; /* Ensure background is set on content too */
}

.flyout-bg-icon {
  position: absolute;
  bottom: -20px;
  right: -20px;
  font-size: 200px !important;
  opacity: 0.03;
  color: white;
  pointer-events: none;
}

.flyout-header h3 {
  color: white;
  font-style: italic;
  font-weight: 900;
  text-transform: uppercase;
  margin-bottom: 20px;
  font-size: 1.2rem;
  letter-spacing: 1px;
}

.flyout-link-item {
  display: flex;
  align-items: center;
  color: #ccc;
  padding: 10px 0;
  cursor: pointer;
  transition: color 0.2s;
  font-size: 0.85rem;
  text-transform: uppercase;
  font-weight: 700;
  letter-spacing: 0.5px;
}

.flyout-link-item:hover {
  color: white;
}
.flyout-link-item.active-link {
  color: white;
  background-color: rgba(255, 255, 255, 0.05);
  border-left: 2px solid #FF4600;
  padding-left: 10px;
}
.flyout-link-item.active-link span {
  color: #FF4600;
  font-weight: 800;
}

.flyout-group-title {
  color: #888;
  font-size: 0.75rem;
  margin: 0;
  text-transform: uppercase;
  font-weight: bold;
}

.flyout-group-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  padding: 10px 0;
  margin-top: 5px;
}

.flyout-group-header:hover .flyout-group-title {
  color: white;
}

.flyout-footer {
  position: absolute;
  bottom: 20px;
  left: 20px;
  right: 20px;
  padding-top: 20px;
  border-top: 1px solid rgba(255, 255, 255, 0.05);
}

.flyout-footer span {
  font-size: 0.65rem;
  color: #555;
  font-weight: 900;
  letter-spacing: 2px;
  font-style: italic;
}
</style>
